/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Network Topology Dashboard
 * Feature #140: Network Topology & Security Dashboard - US1 + US2
 * Constitution: v1.12.0
 *
 * Conformed to Spec 197 design blueprint — migrated the bespoke header, ad-hoc
 * `bg-white … rounded … border` cards, raw <table>/DualScrollTable markup,
 * hand-rolled filter bar, and the hand-rolled `fixed inset-0` VNet detail modal
 * onto the canonical Tailwind+token UI kit (PageLayout / Card / DashboardPanel /
 * FilterBar / DataTable / StatCard / Button / Input / Select / Badge / Modal).
 * The Peering Health + Subnet Capacity widgets use DashboardPanel; the search +
 * Subscription + Resource Group controls use the canonical FilterBar; the VNets
 * table is a page-size-10 DataTable. Presentation only — data fetching
 * (useNetworkOverview / useVnetDetail), filters, sorting, CIDR-overlap detection,
 * the KPI / peering-health / subnet-capacity computations, and the CSV export are
 * unchanged.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Network, Globe2, GitFork, Shield, Lock,
  RefreshCw, Search, ChevronUp, ChevronDown,
  AlertTriangle, CheckCircle2, XCircle, Clock, Ruler,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useNetworkOverview } from '../../hooks/operations/useNetwork';
import { useVnetDetail } from '../../services/operations/networkService';
import { apiClient } from '../../services/apiClient';
import { ExportButton } from '../../components/operations';
import {
  PageLayout,
  Card,
  DataTable,
  DashboardPanel,
  FilterBar,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  LoadingState,
  Modal,
  Extended,
  QuickGlance,
  NotCollected,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type { VNet, Subnet, Peering } from '../../types/network';
import { NoAccessBadge, WorkerVisibilityNotice } from '../../components/collection/WorkerVisibility';
import { formatEastern } from '../../utils/dateFormatter';

// ============================================================================
// Sorting
// ============================================================================

type SortField = 'vnet_name' | 'subscription_name' | 'resource_group' | 'subnet_count' | 'peering_count';
type SortDir = 'asc' | 'desc';

function sortVnets(data: VNet[], field: SortField, dir: SortDir): VNet[] {
  return [...data].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (typeof av === 'number' && typeof bv === 'number') {
      return dir === 'asc' ? av - bv : bv - av;
    }
    const sa = String(av ?? '').toLowerCase();
    const sb = String(bv ?? '').toLowerCase();
    return dir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
  });
}

// ============================================================================
// CIDR Overlap Detection
// ============================================================================

function parseCidr(cidr: string): { start: number; end: number } | null {
  const match = cidr.trim().match(/^(\d+\.\d+\.\d+\.\d+)\/(\d+)$/);
  if (!match) return null;
  const parts = match[1].split('.').map(Number);
  if (parts.some((p) => p < 0 || p > 255)) return null;
  const prefix = Number(match[2]);
  if (prefix < 0 || prefix > 32) return null;
  const ip = ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const start = (ip & mask) >>> 0;
  const end = (start | ~mask) >>> 0;
  return { start, end };
}

function rangesOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start <= b.end && b.start <= a.end;
}

function parseCidrsField(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [raw];
  } catch {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
}

/** A list the collection stores as a JSON string array (address spaces, service endpoints, delegations, DNS servers), as
 *  plain text; '—' when empty (T159 item 23: the pages showed the JSON). */
function listText(raw: string | null | undefined): string {
  if (!raw) return '—';
  const items = parseCidrsField(raw).filter((x) => typeof x === 'string' && x !== '');
  return items.length ? items.join(', ') : '—';
}

/** Returns a map of vnetId -> list of overlapping VNet names */
function detectOverlaps(vnets: VNet[]): Map<string, string[]> {
  const overlapMap = new Map<string, string[]>();
  const parsed = vnets.map((v) => ({
    id: v.id,
    name: v.vnet_name,
    ranges: parseCidrsField(v.address_space_cidrs)
      .map(parseCidr)
      .filter((r): r is { start: number; end: number } => r !== null),
  }));

  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const a = parsed[i];
      const b = parsed[j];
      const hasOverlap = a.ranges.some((ar) => b.ranges.some((br) => rangesOverlap(ar, br)));
      if (hasOverlap) {
        overlapMap.set(a.id, [...(overlapMap.get(a.id) ?? []), b.name]);
        overlapMap.set(b.id, [...(overlapMap.get(b.id) ?? []), a.name]);
      }
    }
  }
  return overlapMap;
}

// ============================================================================
// Sub-components
// ============================================================================

// ============================================================================
// Network Insights panel (Spec 217 T045 / US8, FR-008) — CIDR number-line
// visualization + rollout suggestion + cross-org grouping + honest gap list.
// Additive to the existing per-row overlap badges above; this is the
// dedicated "rollout blocker" callout the brief asks for.
// ============================================================================

interface NetworkInsightsPayload {
  kpis: { cidrOverlapCount: number };
  cidrOverlaps: Array<{ aName: string; aCidr: string; bName: string; bCidr: string }>;
  crossOrgGroups: Array<{ subscriptionName: string; vnetCount: number }>;
  rolloutSuggestion: string | null;
  natGatewayRefs: Array<{ subnetName: string; natGatewayName: string }>;
  notCollected: string[];
}

/** Extract the 2nd octet from a "10.X.0.0/16"-style CIDR for a simple relative number-line position (display only — correctness is computed server-side). */
function octetPosition(cidr: string): number {
  const m = /^\d{1,3}\.(\d{1,3})\./.exec(cidr);
  return m ? Number(m[1]) : 0;
}

export function NetworkInsightsPanel() {
  const { data } = useQuery<NetworkInsightsPayload>({
    queryKey: ['network-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: NetworkInsightsPayload }>('/api/network-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });

  if (!data) return null;

  return (
    <Card className="mb-6">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--color-text-primary)]">
        <Ruler className="h-4 w-4" aria-hidden="true" /> CIDR rollout readiness
      </h3>
      {data.rolloutSuggestion ? (
        <p className="mb-3 text-sm text-[var(--color-status-warning)]">{data.rolloutSuggestion}</p>
      ) : (
        <p className="mb-3 text-sm text-[var(--color-status-success)]">No CIDR overlaps detected — clear to peer.</p>
      )}

      {data.cidrOverlaps.length > 0 && (
        <div className="mb-3 space-y-2">
          {data.cidrOverlaps.map((o, i) => {
            const posA = octetPosition(o.aCidr);
            const posB = octetPosition(o.bCidr);
            return (
              <div key={i} className="text-xs">
                <div className="mb-0.5 flex justify-between text-[var(--color-text-secondary)]">
                  <span>{o.aName} ({o.aCidr})</span>
                  <span>{o.bName} ({o.bCidr})</span>
                </div>
                <div className="relative h-2 w-full rounded bg-[var(--color-bg-subtle)]">
                  <div
                    className="absolute h-2 rounded bg-[var(--color-status-danger)]"
                    style={{ left: `${Math.min(posA, posB) / 2.56}%`, width: `${Math.max(2, Math.abs(posA - posB) / 2.56 + 2)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.crossOrgGroups.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {data.crossOrgGroups.map((g) => (
            <Badge key={g.subscriptionName} variant="secondary" isOutlined size="sm">{g.subscriptionName}: {g.vnetCount} VNets</Badge>
          ))}
        </div>
      )}

      {data.notCollected.length > 0 && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Not collected: {data.notCollected.join(', ')}.
        </p>
      )}
    </Card>
  );
}

const OverlapBadge: React.FC<{ overlappingVnets: string[] }> = ({ overlappingVnets }) => (
  <Badge
    variant="warning"
    leftIcon={<AlertTriangle className="h-3 w-3" />}
    title={`Overlapping address space with: ${overlappingVnets.join(', ')}`}
    className="cursor-help"
  >
    Overlap ({overlappingVnets.length})
  </Badge>
);

const ConnectionStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variant =
    status === 'Connected' ? 'success' : status === 'Disconnected' ? 'error' : 'warning';
  return (
    <Badge variant={variant} isDot>
      {status}
    </Badge>
  );
};

const IpWarningBadge: React.FC<{ available: number | null }> = ({ available }) => {
  if (available == null) return null;
  if (available < 5) {
    return (
      <Badge variant="error" leftIcon={<XCircle className="h-3 w-3" />}>
        Critical
      </Badge>
    );
  }
  if (available < 50) {
    return (
      <Badge variant="warning" leftIcon={<AlertTriangle className="h-3 w-3" />}>
        Warning
      </Badge>
    );
  }
  return null;
};

// ============================================================================
// VNet Detail Modal
// ============================================================================

const VNetDetailModal: React.FC<{
  vnetId: string;
  onClose: () => void;
}> = ({ vnetId, onClose }) => {
  const { data, isLoading } = useVnetDetail(vnetId);
  const [activeTab, setActiveTab] = useState<'subnets' | 'peerings' | 'properties'>('subnets');

  const tabs = [
    { key: 'subnets' as const, label: 'Subnets', count: data?.subnets.length },
    { key: 'peerings' as const, label: 'Peerings', count: data?.peerings.length },
    { key: 'properties' as const, label: 'Properties' },
  ];

  const title = data?.vnet ? (
    <div>
      <div>{data.vnet.vnet_name}</div>
      <div className="text-sm font-normal text-[var(--color-text-secondary)]">
        {data.vnet.subscription_name} / {data.vnet.resource_group}
      </div>
    </div>
  ) : (
    'Loading…'
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={clsx(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.key
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-2 rounded-full bg-[var(--color-bg-subtle)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingState rows={5} />
      ) : !data?.vnet ? (
        <p className="py-4 text-sm text-[var(--color-status-danger)]">VNet not found.</p>
      ) : (
        <>
          {activeTab === 'subnets' && <SubnetsTab subnets={data.subnets} />}
          {activeTab === 'peerings' && <PeeringsTab peerings={data.peerings} />}
          {activeTab === 'properties' && <PropertiesTab vnet={data.vnet} />}
        </>
      )}
    </Modal>
  );
};

// T023: Subnets tab
const SubnetsTab: React.FC<{ subnets: Subnet[] }> = ({ subnets }) => {
  const sorted = useMemo(
    () => [...subnets].sort((a, b) => (a.available_ip_count ?? 0) - (b.available_ip_count ?? 0)),
    [subnets],
  );

  const columns: DataTableColumn<Subnet>[] = [
    { key: 'subnet_name', header: 'Subnet', width: '16%', render: (s) => <span className="font-medium text-[var(--color-text-primary)]">{s.subnet_name}</span> },
    { key: 'address_prefix', header: 'CIDR', width: '12%', render: (s) => <span className="font-mono">{s.address_prefix ?? '—'}</span> },
    {
      key: 'available_ip_count',
      header: 'Available IPs',
      width: '14%',
      render: (s) => (
        <span className="flex items-center gap-2">
          {s.available_ip_count != null ? s.available_ip_count.toLocaleString() : '—'}
          <IpWarningBadge available={s.available_ip_count} />
        </span>
      ),
    },
    { key: 'provisioning_state', header: 'Status', width: '10%', render: (s) => s.provisioning_state },
    { key: 'nsg_name', header: 'NSG', width: '12%', render: (s) => s.nsg_name ?? '—' },
    { key: 'delegations', header: 'Delegations', width: '12%', render: (s) => listText(s.delegations) },
    { key: 'service_endpoints', header: 'Service Endpoints', width: '17%', render: (s) => listText(s.service_endpoints) },
    // Spec 298 FR-006 (row 69): null when the subnet has no snapshot (the repository's OUTER APPLY); "—" as for IPs.
    { key: 'private_endpoint_count', header: 'PEs', width: '7%', align: 'right', render: (s) => s.private_endpoint_count ?? '—' },
  ];

  return (
    <DataTable
      columns={columns}
      rows={sorted}
      rowKey={(s) => s.id}
      minWidth="52rem"
      pageSize={25}
      dense
      emptyMessage="No subnets found."
    />
  );
};

// T024: Peerings tab
const PeeringsTab: React.FC<{ peerings: Peering[] }> = ({ peerings }) => {
  const yn = (v: boolean) => (v ? '✓' : '✗');
  const columns: DataTableColumn<Peering>[] = [
    { key: 'peering_name', header: 'Peering', width: '15%', render: (p) => <span className="font-medium text-[var(--color-text-primary)]">{p.peering_name}</span> },
    { key: 'peering_state', header: 'State', width: '13%', render: (p) => <ConnectionStatusBadge status={p.peering_state} /> },
    { key: 'remote_vnet_name', header: 'Remote VNet', width: '15%', render: (p) => p.remote_vnet_name },
    { key: 'allow_virtual_network_access', header: 'Virtual Access', width: '10%', align: 'center', render: (p) => yn(p.allow_virtual_network_access) },
    { key: 'allow_forwarded_traffic', header: 'Forwarded Traffic', width: '11%', align: 'center', render: (p) => yn(p.allow_forwarded_traffic) },
    { key: 'allow_gateway_transit', header: 'Gateway Transit', width: '10%', align: 'center', render: (p) => yn(p.allow_gateway_transit) },
    { key: 'provisioning_state', header: 'Provisioning', width: '10%', render: (p) => p.provisioning_state },
    { key: 'remote_address_space_cidrs', header: 'Remote CIDRs', width: '11%', render: (p) => listText(p.remote_address_space_cidrs) },
    { key: 'use_remote_gateways', header: 'Remote Gateways', width: '5%', align: 'center', render: (p) => yn(p.use_remote_gateways) },
  ];

  return (
    <DataTable
      columns={columns}
      rows={peerings}
      rowKey={(p) => p.id}
      minWidth="64rem"
      pageSize={25}
      dense
      emptyMessage="No peerings found."
    />
  );
};

// T025: Properties tab
const PropertiesTab: React.FC<{ vnet: VNet }> = ({ vnet }) => {
  const properties = [
    { label: 'Address Space', value: listText(vnet.address_space_cidrs) },
    { label: 'DNS Servers', value: vnet.dns_servers && vnet.dns_servers !== '[]' ? listText(vnet.dns_servers) : 'Default (Azure-provided)' },
    { label: 'DDoS Protection', value: vnet.enable_ddos_protection ? 'Enabled' : 'Disabled' },
    { label: 'Location', value: vnet.location },
    { label: 'Provisioning State', value: vnet.provisioning_state },
    { label: 'Subscription', value: vnet.subscription_name ?? '—' },
    { label: 'Resource Group', value: vnet.resource_group },
    // T159 items 22 and 24: a count no snapshot carries is not collected, never 0; the time reads in Eastern time.
    { label: 'Connected Devices', value: vnet.connected_device_count != null ? String(vnet.connected_device_count) : 'Not collected' },
    { label: 'Snapshot Date', value: vnet.snapshot_date ? formatEastern(vnet.snapshot_date) : '—' },
    { label: 'Azure Resource ID', value: vnet.azure_resource_id || '—' },
  ];

  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {properties.map((p) => (
        <div key={p.label} className="py-2">
          <dt className="text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">{p.label}</dt>
          <dd className="mt-1 break-all text-sm text-[var(--color-text-primary)]">{p.value}</dd>
        </div>
      ))}
    </dl>
  );
};

// ============================================================================
// Sortable header label (renders inside a DataTable column header)
// ============================================================================

const SortHeader: React.FC<{
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onClick: (field: SortField) => void;
}> = ({ label, field, currentField, currentDir, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(field)}
    className="flex items-center gap-1 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
  >
    {label}
    {currentField === field && (currentDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
  </button>
);

// ============================================================================
// Main Page Component (T016-T026)
// ============================================================================

export const NetworkOverviewPage: React.FC = () => {
  const {
    subscription, resourceGroup, search,
    setSubscription, setResourceGroup, setSearch, clearFilters,
    summary, summaryLoading,
    vnets, vnetsLoading,
    refetchAll,
  } = useNetworkOverview();

  const [sortField, setSortField] = useState<SortField>('vnet_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedVnetId, setSelectedVnetId] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedVnets = useMemo(
    () => (vnets ? sortVnets(vnets, sortField, sortDir) : []),
    [vnets, sortField, sortDir],
  );

  // Unique subscription + resource group values for filters
  const subscriptionOptions = useMemo(
    () => [...new Set((vnets ?? []).map((v) => v.subscription_name).filter((n): n is string => !!n))].sort(),
    [vnets],
  );
  const rgOptions = useMemo(
    () => [...new Set((vnets ?? []).map((v) => v.resource_group))].sort(),
    [vnets],
  );

  // Detect CIDR overlaps across all VNets (unfiltered) for consolidation planning
  const overlapMap = useMemo(() => detectOverlaps(vnets ?? []), [vnets]);

  const isLoading = summaryLoading && vnetsLoading;

  const columns: DataTableColumn<VNet>[] = [
    {
      key: 'vnet_name',
      header: <SortHeader label="VNet Name" field="vnet_name" currentField={sortField} currentDir={sortDir} onClick={handleSort} />,
      width: '22%',
      render: (vnet) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="font-medium text-[var(--color-accent)]">{vnet.vnet_name}</span>
          {vnet.no_access_since && <NoAccessBadge since={vnet.no_access_since} />}
        </span>
      ),
    },
    {
      key: 'subscription_name',
      header: <SortHeader label="Subscription" field="subscription_name" currentField={sortField} currentDir={sortDir} onClick={handleSort} />,
      width: '22%',
      render: (vnet) => <span className="text-[var(--color-text-secondary)]">{vnet.subscription_name}</span>,
    },
    {
      key: 'resource_group',
      header: <SortHeader label="Resource Group" field="resource_group" currentField={sortField} currentDir={sortDir} onClick={handleSort} />,
      width: '20%',
      render: (vnet) => <span className="text-[var(--color-text-secondary)]">{vnet.resource_group}</span>,
    },
    {
      key: 'address_space_cidrs',
      header: 'Address Space',
      width: '22%',
      render: (vnet) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[var(--color-text-secondary)]">{listText(vnet.address_space_cidrs)}</span>
          {overlapMap.has(vnet.id) && <OverlapBadge overlappingVnets={overlapMap.get(vnet.id)!} />}
        </span>
      ),
    },
    {
      key: 'subnet_count',
      header: <SortHeader label="Subnets" field="subnet_count" currentField={sortField} currentDir={sortDir} onClick={handleSort} />,
      width: '7%',
      align: 'right',
      render: (vnet) => <span className="text-[var(--color-text-secondary)]">{vnet.subnet_count}</span>,
    },
    {
      key: 'peering_count',
      header: <SortHeader label="Peerings" field="peering_count" currentField={sortField} currentDir={sortDir} onClick={handleSort} />,
      width: '7%',
      align: 'right',
      render: (vnet) => <span className="text-[var(--color-text-secondary)]">{vnet.peering_count}</span>,
    },
  ];

  if (isLoading) {
    return (
      <PageLayout
      helpId="auto.network-overview"
        title="Network Insights"
        description="Cross-subscription view of Azure networking resources"
        icon={<Network className="h-6 w-6" />}
      >
        <LoadingState rows={8} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      helpId="auto.network-overview"
      // Spec 286 T159 item 21: the label of the menu entry that opens this page (and the breadcrumb), not the group's.
      title="Network Insights"
      description="Cross-subscription view of Azure networking resources"
      icon={<Network className="h-6 w-6" />}
      onRefresh={() => { void refetchAll(); }}
      isRefreshing={summaryLoading || vnetsLoading}
      actions={
        <>
          <ExportButton
            data={sortedVnets}
            filename="network-topology-vnets"
            columns={[
              { key: 'vnet_name', label: 'VNet Name' },
              { key: 'subscription_name', label: 'Subscription' },
              { key: 'resource_group', label: 'Resource Group' },
              { key: 'address_space_cidrs', label: 'Address Space' },
              { key: 'location', label: 'Location' },
              { key: 'subnet_count', label: 'Subnets' },
              { key: 'peering_count', label: 'Peerings' },
              { key: 'provisioning_state', label: 'State' },
            ]}
          />
        </>
      }
    >
      {summary && (
        <WorkerVisibilityNotice
          since={summary.noAccessSince}
          counts={[
            { noun: 'vnets', noAccess: summary.noAccessVnets, total: summary.totalVnets },
            { noun: 'subnets', noAccess: summary.noAccessSubnets, total: summary.totalSubnets },
            { noun: 'private endpoints', noAccess: summary.noAccessPrivateEndpoints, total: summary.totalPrivateEndpoints },
            { noun: 'NSGs', noAccess: summary.noAccessNsgs, total: summary.totalNsgs },
          ]}
        />
      )}
      {/* Summary Cards (T017) */}
      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          <StatCard label="VNets" value={summary.totalVnets} icon={<Network className="h-5 w-5 text-[var(--color-accent)]" />} />
          <StatCard label="Subnets" value={summary.totalSubnets} icon={<Globe2 className="h-5 w-5 text-[var(--color-accent)]" />} />
          <StatCard label="Peerings" value={summary.totalPeerings} icon={<GitFork className="h-5 w-5 text-[var(--color-accent)]" />} />
          <StatCard label="Private Endpoints" value={summary.totalPrivateEndpoints} icon={<Lock className="h-5 w-5 text-[var(--color-status-success)]" />} />
          <StatCard label="NSGs" value={summary.totalNsgs} icon={<Shield className="h-5 w-5 text-[var(--color-status-warning)]" />} />
        </div>
      )}

      {/* Spec 217 T045: dedicated CIDR rollout-readiness panel */}
      <NetworkInsightsPanel />

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      {/* Address Space Overlap Warning */}
      {overlapMap.size > 0 && (
        <Card className="mb-6 border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--color-status-warning)]" />
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Address Space Overlap Detected</h3>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                {overlapMap.size} VNet{overlapMap.size === 1 ? ' has' : 's have'} overlapping CIDR ranges.
                This will cause routing conflicts if these VNets are peered or consolidated.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Peering Health + Subnet Capacity (T018-T019) */}
      {summary && (
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Peering Health */}
          <DashboardPanel title="Peering Health">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <CheckCircle2 className="h-4 w-4 text-[var(--color-status-success)]" /> Connected
                </span>
                <span className="text-sm font-semibold text-[var(--color-text-primary)]">{summary.connectedPeerings}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <XCircle className="h-4 w-4 text-[var(--color-status-danger)]" /> Disconnected
                </span>
                <span className="text-sm font-semibold text-[var(--color-text-primary)]">{summary.disconnectedPeerings}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <Clock className="h-4 w-4 text-[var(--color-status-warning)]" /> Initiated
                </span>
                <span className="text-sm font-semibold text-[var(--color-text-primary)]">{summary.initiatedPeerings}</span>
              </div>
            </div>
          </DashboardPanel>

          {/* Subnet Capacity */}
          <DashboardPanel title="Subnet Capacity">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-text-secondary)]">Total Available IPs</span>
                {summary.totalAvailableIps != null ? (
                  <span className="text-sm font-semibold text-[var(--color-text-primary)]">{summary.totalAvailableIps.toLocaleString()}</span>
                ) : (
                  <NotCollected reason="No subnet's latest snapshot carries an available-IP count." />
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                  <AlertTriangle className="h-4 w-4 text-[var(--color-status-warning)]" /> Subnets Low Capacity (&lt;50 IPs)
                </span>
                {summary.subnetsLowCapacity != null ? (
                  <span className={clsx(
                    'text-sm font-semibold',
                    summary.subnetsLowCapacity > 0 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-text-primary)]'
                  )}>
                    {summary.subnetsLowCapacity}
                  </span>
                ) : (
                  <NotCollected reason="No subnet's latest snapshot carries an available-IP count." />
                )}
              </div>
              {summary.snapshotDate && (
                <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-2">
                  <span className="text-xs text-[var(--color-text-secondary)]">Snapshot Date</span>
                  <span className="text-xs text-[var(--color-text-secondary)]">{formatEastern(summary.snapshotDate)}</span>
                </div>
              )}
            </div>
          </DashboardPanel>
        </div>
      )}

      {/* Filter Bar (T020) — canonical FilterBar toolbar */}
      <FilterBar trailing={
        <Button variant="ghost" onClick={clearFilters}>
          Clear Filters
        </Button>
      }>
        <div className="min-w-[16rem] flex-1">
          <Input
            isFullWidth
            label="Search VNets"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="min-w-[14rem]">
          <Select
            isFullWidth
            label="Subscription"
            value={subscription}
            onChange={(e) => setSubscription(e.target.value)}
            options={[{ value: '', label: 'All Subscriptions' }, ...subscriptionOptions.map((s) => ({ value: s, label: s }))]}
          />
        </div>
        <div className="min-w-[12rem]">
          <Select
            isFullWidth
            label="Resource Group"
            value={resourceGroup}
            onChange={(e) => setResourceGroup(e.target.value)}
            options={[{ value: '', label: 'All Resource Groups' }, ...rgOptions.map((rg) => ({ value: rg, label: rg }))]}
          />
        </div>
      </FilterBar>

      {/* VNet Table (T021) */}
      <Card padding="none">
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-6 py-4">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Virtual Networks</h2>
          {vnets && (
            <span className="text-sm font-normal text-[var(--color-text-secondary)]">({vnets.length} total)</span>
          )}
        </div>
        {vnetsLoading ? (
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={sortedVnets}
            rowKey={(vnet) => vnet.id}
            minWidth="52rem"
            pageSize={10}
            onRowClick={(vnet) => setSelectedVnetId(vnet.id)}
            emptyMessage="No network data collected yet."
          />
        )}
      </Card>
      </Extended>

      {/* VNet Detail Modal (T022) — kit <Modal>: transparent-but-blocking,
          movable by the header, outside-click does NOT dismiss. */}
      {selectedVnetId !== null && (
        <VNetDetailModal vnetId={selectedVnetId} onClose={() => setSelectedVnetId(null)} />
      )}
    </PageLayout>
  );
};

export default NetworkOverviewPage;
